import React, { useRef, useState, useEffect } from 'react';
import { FaWarehouse } from 'react-icons/fa';
import './StoreMap.css';

// Generate 50 racks for each shelf in Area A
const generateRacks = (area, shelf, count = 50) =>
  Array.from({ length: count }, (_, i) => [{ code: `${area}-${shelf}-${i + 1}`, qty: Math.floor(Math.random() * 20) }]);

// New warehouse data structure: shelf and rack have 1-to-1 data (each rack contains exactly one bin)
const warehouse = [
  {
    area: 'A',
    shelves: [
      { shelf: 1, racks: generateRacks('A', 1, 50) },
      { shelf: 2, racks: generateRacks('A', 2, 50) },
      { shelf: 3, racks: generateRacks('A', 3, 50) },
    ]
  },
  {
    area: 'B',
    shelves: [
      {
        shelf: 1,
        racks: [
          [{ code: 'B-1-1', qty: 8 }],
          [{ code: 'B-1-2', qty: 0 }],
          [{ code: 'B-1-3', qty: 4 }],
          [{ code: 'B-1-4', qty: 2 }],
        ]
      },
      {
        shelf: 2,
        racks: [
          [{ code: 'B-2-1', qty: 1 }],
          [{ code: 'B-2-2', qty: 6 }],
          [{ code: 'B-2-3', qty: 0 }],
          [{ code: 'B-2-4', qty: 5 }],
        ]
      },
      {
        shelf: 3,
        racks: [
          [{ code: 'B-3-1', qty: 3 }],
          [{ code: 'B-3-2', qty: 7 }],
          [{ code: 'B-3-3', qty: 2 }],
          [{ code: 'B-3-4', qty: 0 }],
        ]
      }
    ]
  }
];

function getBinColor(qty) {
  if (qty === 0) return 'empty';
  if (qty < 5) return 'low';
  return 'normal';
}

function openBinModal(bin) {
  alert(`Bin details: ${JSON.stringify(bin, null, 2)}`);
}

const StoreMap = ({ data = warehouse, sectionName = 'Store View', onFilter, onFullscreenChange }) => {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedAreaIdx, setSelectedAreaIdx] = useState(0);
  const [showAllRacksModal, setShowAllRacksModal] = useState(false);

  const selectedArea = data[selectedAreaIdx];
  const rackCount = selectedArea.shelves[0]?.racks.length || 0;
  const rackLabels = Array.from({ length: rackCount }, (_, i) => `Rack ${i + 1}`);
  const showAddMore = rackCount > 10;

  useEffect(() => {
    function handleFullscreenChange() {
      const isFs = document.fullscreenElement === containerRef.current;
      setIsFullscreen(isFs);
      if (onFullscreenChange) onFullscreenChange(isFs);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [onFullscreenChange]);

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Helper to render a grid (used for both main and modal views)
  const renderGrid = (shelves, rackLabels, sliceTo = null) => (
    <div className="rack-scroll-area">
      <div className="rack-labels-row">
        <div className="corner-label" />
        {(sliceTo ? rackLabels.slice(0, sliceTo) : rackLabels).map((_, idx) => (
          <div className="rack-label" key={idx}>R{idx + 1}</div>
        ))}
      </div>
      {shelves.map((shelf, shelfIdx) => (
        <div className="shelf-row" key={shelf.shelf}>
          <div className="shelf-label">Shelf {shelf.shelf}</div>
          {(sliceTo ? shelf.racks.slice(0, sliceTo) : shelf.racks).map((bins, rackIdx) => (
            <div className="rack-cell" key={`rack${rackIdx}-shelf${shelf.shelf}`}>
              {bins && bins.length > 0 ? (
                <div className="bin-multi-row">
                  {bins.map((bin, idx) => (
                    <div className={`bin-box ${getBinColor(bin.qty)}`} key={bin.code + idx}>
                      <span className="bin-code">{bin.code}</span>
                      <span className="bin-qty">{bin.qty}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bin-box empty" />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="store-rack-system" ref={containerRef}>
      <div className="store-view-header">
        <FaWarehouse className="store-view-icon" />
        <span>Store View</span>
        <button className="fullscreen-btn" onClick={handleFullscreen} title="Toggle Fullscreen">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/><path d="M8 21H5a2 2 0 0 1-2-2v-3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
        </button>
      </div>
      {isFullscreen && (
        <div className="fullscreen-indicator">Fullscreen Mode</div>
      )}
      <h2 className="store-view-heading">{sectionName}</h2>
      {/* Area Selector */}
      <div className="area-tabs">
        {data.map((area, idx) => (
          <button
            key={area.area}
            className={`area-tab${idx === selectedAreaIdx ? ' active' : ''}`}
            onClick={() => setSelectedAreaIdx(idx)}
          >
            Area {area.area}
          </button>
        ))}
      </div>
      {/* Main Grid: Only show first 20 racks */}
      {renderGrid(selectedArea.shelves, rackLabels, 10)}
      {/* Add More Button */}
      {showAddMore && (
        <div style={{ textAlign: 'center', margin: '16px 0' }}>
          <button className="add-more-btn" onClick={() => setShowAllRacksModal(true)}>
            Show All Racks (Full Screen)
          </button>
        </div>
      )}
      {/* Modal for all racks */}
      {showAllRacksModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>All Racks - Area {selectedArea.area}</h2>
              <button className="modal-close-btn" onClick={() => setShowAllRacksModal(false)}>&times;</button>
            </div>
            {renderGrid(selectedArea.shelves, rackLabels)}
          </div>
        </div>
      )}
      <div className="store-legend">
        <span className="legend-box normal"></span> Normal
        <span className="legend-box low"></span> Low Stock
        <span className="legend-box empty"></span> Empty
      </div>
    </div>
  );
};

export default StoreMap; 